import React, { useState, useEffect } from 'react';
import { API_ENDPOINTS } from '../../config/api';
import axios from '../../config/axiosConfig';
import LoadingOverlay from '../LoadingOverlay';
import Toast from '../Toast';
import { normalizeRestaurantId } from '../../utils/restaurantUtils';

const CookHome = ({ cookId, restaurantId: restaurantIdProp, onNavigate }) => {
    const restaurantId = normalizeRestaurantId(restaurantIdProp);
    const [stats, setStats] = useState({
        activeOrdersCount: 0,
        completedOrdersToday: 0,
        totalOrdersToday: 0,
        readyOrdersCount: 0
    });
    const [attendance, setAttendance] = useState(null);
    const [loading, setLoading] = useState(true);
    const [actionLoading, setActionLoading] = useState(false);
    const [loadingMessage, setLoadingMessage] = useState('');
    const [toast, setToast] = useState({ show: false, message: '', type: 'success' });

    useEffect(() => {
        if (cookId && restaurantId) {
            fetchDashboardData();
        }
    }, [cookId, restaurantId]);

    const fetchDashboardData = async () => {
        try {
            setLoading(true);
            const [statsResponse, attendanceResponse] = await Promise.all([
                axios.get(API_ENDPOINTS.COOK_STATS, {
                    params: { cookId, restaurantId }
                }),
                axios.get(API_ENDPOINTS.ATTENDANCE_GET, {
                    params: { staffId: cookId, date: new Date().toISOString().split('T')[0] }
                })
            ]);

            setStats(statsResponse.data);
            if (attendanceResponse.data && attendanceResponse.data.length > 0) {
                setAttendance(attendanceResponse.data[0]);
            }
        } catch (error) {
            console.error('Failed to fetch dashboard data:', error);
        } finally {
            setLoading(false);
        }
    };

    const showToast = (message, type = 'success') => {
        setToast({ show: true, message, type });
        setTimeout(() => setToast({ show: false, message: '', type: 'success' }), 3000);
    };

    const handleCheckIn = async (shift) => {
        setActionLoading(true);
        setLoadingMessage('Checking in...');
        try {
            await axios.post(API_ENDPOINTS.ATTENDANCE_CHECKIN, {
                staffId: cookId,
                shift,
                restaurantId
            });
            showToast('Checked in successfully', 'success');
            await fetchDashboardData();
        } catch (error) {
            showToast(error.response?.data?.message || 'Check-in failed', 'error');
        } finally {
            setActionLoading(false);
        }
    };

    const handleCheckOut = async () => {
        if (!attendance) return;
        setActionLoading(true);
        setLoadingMessage('Checking out...');
        try {
            await axios.post(API_ENDPOINTS.ATTENDANCE_CHECKOUT, {
                staffId: cookId,
                shift: attendance.shift
            });
            showToast('Checked out successfully', 'success');
            await fetchDashboardData();
        } catch (error) {
            showToast(error.response?.data?.message || 'Check-out failed', 'error');
        } finally {
            setActionLoading(false);
        }
    };

    const getCurrentShift = () => {
        const hour = new Date().getHours();
        if (hour >= 9 && hour < 15) return 'MORNING';
        if (hour >= 15 && hour < 22) return 'EVENING';
        return 'NIGHT';
    };

    const currentShift = getCurrentShift();
    const isCheckedIn = attendance && !attendance.checkOutTime;

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-4 border-orange-500 border-t-transparent mx-auto mb-4"></div>
                    <p className="text-gray-600">Loading dashboard...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="relative space-y-4 sm:space-y-6">
            {actionLoading && <LoadingOverlay message={loadingMessage} />}

            {/* Shift Status Card */}
            <div className="bg-gradient-to-r from-orange-600 to-red-600 rounded-xl shadow-xl p-4 sm:p-6 text-white">
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                    <div className="flex-1">
                        <h2 className="text-xl sm:text-2xl font-bold mb-2">Today's Shift</h2>
                        <div className="flex items-center gap-2">
                            {isCheckedIn ? (
                                <>
                                    <span className="text-2xl sm:text-3xl">🟢</span>
                                    <div>
                                        <span className="font-semibold text-lg">Checked In</span>
                                        {attendance.checkInTime && (
                                            <p className="text-xs sm:text-sm text-orange-100">
                                                Since {new Date(attendance.checkInTime).toLocaleTimeString()}
                                            </p>
                                        )}
                                    </div>
                                </>
                            ) : (
                                <>
                                    <span className="text-2xl sm:text-3xl">🔴</span>
                                    <span className="font-semibold text-lg">Not Checked In</span>
                                </>
                            )}
                        </div>
                    </div>
                    <div>
                        {!isCheckedIn ? (
                            <button
                                onClick={() => handleCheckIn(currentShift)}
                                className="bg-white text-orange-600 px-6 py-3 rounded-lg font-bold hover:bg-orange-50 transition-all shadow-lg hover:shadow-xl text-sm sm:text-base"
                            >
                                ✓ Check In
                            </button>
                        ) : (
                            <button
                                onClick={handleCheckOut}
                                className="bg-red-500 text-white px-6 py-3 rounded-lg font-bold hover:bg-red-600 transition-all shadow-lg hover:shadow-xl text-sm sm:text-base"
                            >
                                ✗ Check Out
                            </button>
                        )}
                    </div>
                </div>
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
                <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6 border-l-4 border-orange-500 hover:shadow-xl transition-shadow">
                    <div className="flex items-center justify-between mb-2">
                        <div className="flex-1">
                            <p className="text-gray-600 text-xs sm:text-sm mb-1">Active Orders</p>
                            <p className="text-2xl sm:text-3xl font-bold text-gray-800">{stats.activeOrdersCount}</p>
                        </div>
                        <span className="text-3xl sm:text-4xl">🍳</span>
                    </div>
                    <button
                        onClick={() => onNavigate && onNavigate('live')}
                        className="mt-2 text-orange-600 text-xs sm:text-sm hover:underline font-medium"
                    >
                        View Orders →
                    </button>
                </div>

                <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6 border-l-4 border-green-500 hover:shadow-xl transition-shadow">
                    <div className="flex items-center justify-between mb-2">
                        <div className="flex-1">
                            <p className="text-gray-600 text-xs sm:text-sm mb-1">Ready Orders</p>
                            <p className="text-2xl sm:text-3xl font-bold text-gray-800">{stats.readyOrdersCount}</p>
                        </div>
                        <span className="text-3xl sm:text-4xl">✅</span>
                    </div>
                </div>

                <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6 border-l-4 border-blue-500 hover:shadow-xl transition-shadow">
                    <div className="flex items-center justify-between mb-2">
                        <div className="flex-1">
                            <p className="text-gray-600 text-xs sm:text-sm mb-1">Completed Today</p>
                            <p className="text-2xl sm:text-3xl font-bold text-gray-800">{stats.completedOrdersToday}</p>
                        </div>
                        <span className="text-3xl sm:text-4xl">📦</span>
                    </div>
                </div>

                <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6 border-l-4 border-purple-500 hover:shadow-xl transition-shadow">
                    <div className="flex items-center justify-between mb-2">
                        <div className="flex-1">
                            <p className="text-gray-600 text-xs sm:text-sm mb-1">Total Today</p>
                            <p className="text-2xl sm:text-3xl font-bold text-gray-800">{stats.totalOrdersToday}</p>
                        </div>
                        <span className="text-3xl sm:text-4xl">📊</span>
                    </div>
                </div>
            </div>

            {/* Quick Actions */}
            <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6">
                <h3 className="text-lg sm:text-xl font-bold mb-4">Quick Actions</h3>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
                    <button
                        onClick={() => onNavigate && onNavigate('attendance')}
                        className="p-4 sm:p-6 border-2 border-gray-200 rounded-xl hover:border-orange-500 hover:bg-orange-50 transition-all text-center group"
                    >
                        <span className="text-3xl sm:text-4xl block mb-2 group-hover:scale-110 transition-transform">📅</span>
                        <span className="text-xs sm:text-sm font-medium text-gray-700">Attendance</span>
                    </button>
                    <button
                        onClick={() => onNavigate && onNavigate('live')}
                        className="p-4 sm:p-6 border-2 border-gray-200 rounded-xl hover:border-orange-500 hover:bg-orange-50 transition-all text-center group"
                    >
                        <span className="text-3xl sm:text-4xl block mb-2 group-hover:scale-110 transition-transform">🍳</span>
                        <span className="text-xs sm:text-sm font-medium text-gray-700">Kitchen Orders</span>
                    </button>
                    <button
                        onClick={() => onNavigate && onNavigate('live')}
                        className="p-4 sm:p-6 border-2 border-gray-200 rounded-xl hover:border-orange-500 hover:bg-orange-50 transition-all text-center group"
                    >
                        <span className="text-3xl sm:text-4xl block mb-2 group-hover:scale-110 transition-transform">📋</span>
                        <span className="text-xs sm:text-sm font-medium text-gray-700">View Orders</span>
                    </button>
                    <button
                        onClick={() => onNavigate && onNavigate('performance')}
                        className="p-4 sm:p-6 border-2 border-gray-200 rounded-xl hover:border-orange-500 hover:bg-orange-50 transition-all text-center group"
                    >
                        <span className="text-3xl sm:text-4xl block mb-2 group-hover:scale-110 transition-transform">📊</span>
                        <span className="text-xs sm:text-sm font-medium text-gray-700">Performance</span>
                    </button>
                </div>
            </div>

            {/* Info Card */}
            <div className="bg-gradient-to-r from-orange-50 to-red-50 border-2 border-orange-200 rounded-xl p-4 sm:p-6">
                <div className="flex items-start gap-3">
                    <span className="text-2xl sm:text-3xl">💡</span>
                    <div>
                        <h4 className="font-bold text-gray-800 mb-1 text-sm sm:text-base">Quick Tips</h4>
                        <ul className="text-xs sm:text-sm text-gray-700 space-y-1">
                            <li>• Check in to start receiving order assignments</li>
                            <li>• Update order status as you progress through preparation</li>
                            <li>• Call waiter when order is ready for pickup</li>
                            <li>• Add notes if you need clarification from customer</li>
                        </ul>
                    </div>
                </div>
            </div>

            {toast.show && <Toast message={toast.message} type={toast.type} />}
        </div>
    );
};

export default CookHome;
